/**
 * A reply the assistant suggested in a private room (TMMAPP-0148 U1 item 4; TMMAPI-0184 U1
 * `replyDraft`): a card under the answer, with two actions.
 * - **Copy** puts the text on the clipboard and announces "Copied" politely.
 * - **Post to {shared session}** opens **Bring something back?** with the text in its note
 *   (TMMAPP-0153 item 10). The person edits it and chooses Share & return, or returns without
 *   sharing. **Nothing posts by itself:** this card never calls the messages route.
 * The text renders as plain text. Post is absent when the shared session isn't available.
 */
import { useEffect, useRef, useState } from 'react';
import { Box, Button, Paper, Stack, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CopyIcon } from '@/components/icons';
import { LiveRegion } from '@/components/LiveRegion';
import { resolveTokens } from '@/theme/theme';

/** How long "Copied" shows on the button. */
const COPIED_MS = 2000;

interface ReplyDraftCardProps {
  /** The answer's line id, for the card's heading id. */
  messageId: number;
  text: string;
  /** The shared session's name, for Post's label; null hides Post. */
  sharedName: string | null;
  onPost?: ((text: string) => void) | undefined;
}

export function ReplyDraftCard({ messageId, text, sharedName, onPost }: ReplyDraftCardProps) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const [copy, setCopy] = useState<'idle' | 'copied' | 'failed'>('idle');
  const [announcement, setAnnouncement] = useState({ key: 0, text: '' });
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const titleId = `reply-draft-${messageId}`;

  async function copyText() {
    let next: 'copied' | 'failed' = 'copied';
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // No Clipboard API (an insecure context) or permission denied: the text is on screen to select.
      next = 'failed';
    }
    setCopy(next);
    setAnnouncement((current) => ({
      key: current.key + 1,
      text: next === 'copied' ? t('assistant.breakout.copied') : t('assistant.breakout.copyFailed'),
    }));
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setCopy('idle'), COPIED_MS);
  }

  return (
    <Paper
      component="section"
      aria-labelledby={titleId}
      sx={{
        mt: 1.5,
        p: 1.5,
        borderRadius: 1.5,
        border: `1px solid ${tokens.line}`,
        bgcolor: tokens.surface,
      }}
      data-reply-draft=""
    >
      <Typography id={titleId} component="h3" sx={{ m: 0, fontSize: 14, fontWeight: 600 }}>
        {t('assistant.breakout.draftTitle')}
      </Typography>
      <Typography
        variant="body2"
        sx={{ mt: 0.75, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
        data-reply-draft-text=""
      >
        {text}
      </Typography>
      {copy === 'failed' && (
        <Typography sx={{ mt: 0.75, fontSize: 12, color: tokens.text2 }}>
          {t('assistant.breakout.copyFailed')}
        </Typography>
      )}
      <Stack direction="row" sx={{ mt: 1.25, gap: 1, flexWrap: 'wrap' }}>
        <Button size="small" startIcon={<CopyIcon />} onClick={() => void copyText()}>
          {copy === 'copied' ? t('assistant.breakout.copied') : t('assistant.breakout.copy')}
        </Button>
        {sharedName !== null && onPost !== undefined && (
          <Button size="small" variant="outlined" onClick={() => onPost(text)}>
            {t('assistant.breakout.post', { sharedName })}
          </Button>
        )}
      </Stack>
      <Box>
        <LiveRegion key={announcement.key} text={announcement.text} />
      </Box>
    </Paper>
  );
}
